// Design lint: layout and legibility problems that are valid IR but read badly. Warnings only;
// nothing here blocks saving, so PHP has no twin. Runs on spec bodies (static structure) and on
// materialised documents (where bound rows and values are known).
import { getPreset, type Intent } from './tokens.ts'
import { CONTAINER_TYPES } from './document.ts'
import { ITEM_WIDTH_PX, isResponsiveMap } from './layout.ts'

export const DESIGN_RULES = ['table-overflow', 'palette-overflow', 'empty-panel', 'contrast', 'density', 'convention', 'responsive'] as const
export type DesignRule = typeof DESIGN_RULES[number]

export interface DesignWarning { path: string, rule: DesignRule, message: string }

export interface DesignLintOptions { preset?: string | null }

const TABLE_COLUMNS_WIDE = 8
const TABLE_COLUMNS_NARROW = 5
const GRID_MAX_CHILDREN = 12
const GRID_MAX_HEAVY_COLUMNS = 3
const MAX_CHARTS = 10
const MAX_SECTION_DEPTH = 4
const TEXT_CONTRAST = 3
const MARK_CONTRAST = 2
const THIN_MARK_KINDS = ['line', 'scatter', 'radar', 'graph']
/** A phone: the narrowest width a tool is expected to fit */
const PHONE_WIDTH = 360
const GRID_MAX_FIXED_COLUMNS = 4
/** Containers whose children may be empty without anything looking broken */
const OPTIONAL_CHILDREN = ['empty', 'use']

type Node = Record<string, any>

// ---------------------------------------------------------------------------
// Contrast (WCAG 2 relative luminance)

function luminance (hex: string): number | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex)
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  const channel = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255)
}

export function contrastRatio (a: string, b: string): number | null {
  const la = luminance(a)
  const lb = luminance(b)
  if (la === null || lb === null) return null
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

// ---------------------------------------------------------------------------

function seriesCount (chart: Node): number {
  if (Array.isArray(chart.series)) return chart.series.length
  if (Array.isArray(chart.items)) return chart.items.length
  if (chart.kind === 'sankey' && Array.isArray(chart.nodes)) return chart.nodes.length
  return 0
}

function explicitIntents (chart: Node): boolean {
  const list = Array.isArray(chart.series) ? chart.series : Array.isArray(chart.items) ? chart.items : []
  return list.length > 0 && list.every((x: Node) => x && x.intent && x.intent !== 'neutral')
}

function textIntents (node: Node): Intent[] {
  const out: Intent[] = []
  if ((node.type === 'metric' || node.type === 'score') && node.intent) out.push(node.intent)
  for (const rule of Array.isArray(node.rules) ? node.rules : []) {
    if (rule && rule.intent) out.push(rule.intent)
  }
  return out
}

const KIND_WORDS = new Set([
  'tool', 'report', 'dashboard', 'view', 'interface', 'overview', 'summary',
  'työkalu', 'raportti', 'kojelauta', 'näkymä', 'yhteenveto',
  'verktyg', 'rapport', 'översikt', 'vy', 'sammanfattning'
])

function emptyEyebrow (eyebrow: string, title: unknown): boolean {
  const words = eyebrow.trim().toLowerCase()
  return KIND_WORDS.has(words) || (typeof title === 'string' && title.trim().toLowerCase() === words)
}

export function lintDesign (body: unknown, options: DesignLintOptions = {}): DesignWarning[] {
  const preset = getPreset(options.preset)
  const surface = preset.tokens['color-surface']
  const intentColor: Record<string, string> = {
    neutral: preset.tokens['color-neutral'],
    accent: preset.tokens['color-accent'],
    positive: preset.tokens['color-positive'],
    warning: preset.tokens['color-warning'],
    negative: preset.tokens['color-negative'],
    info: preset.tokens['color-info']
  }
  const warnings: DesignWarning[] = []
  const warn = (path: string, rule: DesignRule, message: string) => warnings.push({ path, rule, message })
  const warnedIntents = new Set<string>()
  let charts = 0

  const visit = (nodes: unknown, path: string, narrow: boolean, sectionDepth: number) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach((raw, i) => {
      if (!raw || typeof raw !== 'object') return
      const node = raw as Node
      const p = `${path}[${i}]`

      for (const intent of textIntents(node)) {
        const ratio = contrastRatio(intentColor[intent] ?? '', surface)
        if (ratio !== null && ratio < TEXT_CONTRAST && !warnedIntents.has(intent)) {
          warnedIntents.add(intent)
          warn(p, 'contrast', `the ${intent} colour has ${ratio.toFixed(1)}:1 contrast on the ${preset.id} preset; values in it are hard to read`)
        }
      }

      if (node.type === 'table') {
        const columns = Array.isArray(node.columns) ? node.columns.length : 0
        const limit = narrow ? TABLE_COLUMNS_NARROW : TABLE_COLUMNS_WIDE
        if (columns > limit) {
          warn(p, 'table-overflow', `${columns} columns${narrow ? ' in a narrow panel' : ''} will scroll sideways; keep ${limit} or fewer, or give the table its own row`)
        }
        if (Array.isArray(node.rows) && node.rows.length === 0) {
          warn(p, 'empty-panel', 'the table has no rows')
        }
      }

      if ((node.type === 'heading' || node.type === 'section') && typeof node.eyebrow === 'string' && emptyEyebrow(node.eyebrow, node.text ?? node.title)) {
        warn(p, 'convention', 'the eyebrow only names the kind of view or repeats the title; leave it out, or use it for context such as the period or group')
      }

      if (node.type === 'stack' && node.direction === 'horizontal' && Array.isArray(node.children) && node.children.length &&
          node.children.every((c: Node) => c?.type === 'field' && (c.input === 'search' || c.input === 'select')) &&
          node.children.some((c: Node) => c.input === 'search')) {
        warn(p, 'convention', 'POS lists put search and select filters in a filter row; use a filterbar node')
      }

      if (node.type === 'button' && !node.icon && node.variant !== 'link') {
        warn(p, 'convention', 'POS buttons show an icon before the label; set icon, e.g. save with intent positive for a save button')
      }

      if (node.type === 'datatable' && Array.isArray(node.rowActions)) {
        node.rowActions.forEach((ra: Node, j: number) => {
          const click = ra?.on?.click
          if (Array.isArray(click) && click.length === 1 && click[0]?.do === 'navigate') {
            warn(`${p}.rowActions[${j}]`, 'convention', 'POS lists open a record by clicking its row, not with a button; use datatable.open instead')
          }
        })
      }

      if (node.type === 'metric' && (node.value === null || node.value === '')) {
        warn(p, 'empty-panel', 'the key figure shows no value')
      }

      if (node.type === 'chart' && node.chart && typeof node.chart === 'object') {
        charts++
        const chart = node.chart as Node
        const count = seriesCount(chart)
        if (count > preset.palette.length && !explicitIntents(chart)) {
          warn(p, 'palette-overflow', `${count} colours needed but the ${preset.id} palette has ${preset.palette.length}; colours will repeat. Group the smallest into "Other" or split the chart`)
        }
        if (Array.isArray(chart.series) && chart.series.length && chart.series.every((x: Node) => Array.isArray(x?.data) && x.data.every((v: unknown) => v === null))) {
          warn(p, 'empty-panel', 'the chart has no values')
        }
        const thin = THIN_MARK_KINDS.includes(chart.kind) || (Array.isArray(chart.series) && chart.series.some((x: Node) => x?.render === 'line'))
        if (thin) {
          const faint = preset.palette.slice(0, Math.max(1, count)).findIndex(color => (contrastRatio(color, surface) ?? 21) < MARK_CONTRAST)
          if (faint !== -1) {
            warn(p, 'contrast', `series ${faint + 1} uses a colour with low contrast on the ${preset.id} preset; thin lines and points in it are hard to see`)
          }
        }
        if (node.height === 'sm' && count > 6) {
          warn(p, 'density', `${count} series in a small chart; use height "md" or fewer series`)
        }
      }

      if (node.type === 'grid' && typeof node.columns === 'number' && node.columns > GRID_MAX_FIXED_COLUMNS) {
        warn(p, 'responsive', `${node.columns} columns squeeze on a tablet; set columns per width, e.g. {"base": 1, "md": 2, "lg": ${node.columns}}`)
      }
      if ((node.type === 'split' || node.type === 'sidebar') && node.collapseBelow === 'never') {
        warn(p, 'responsive', `a ${node.type} that never stacks overflows on a phone; leave collapseBelow out or use sm`)
      }
      if (node.type === 'row' && node.wrap === false && !node.stackBelow && Array.isArray(node.children)) {
        const fixed = node.children.reduce((sum: number, c: Node) => sum + (ITEM_WIDTH_PX[c?.width as keyof typeof ITEM_WIDTH_PX] ?? 0), 0)
        if (fixed > PHONE_WIDTH) warn(p, 'responsive', `fixed widths add up to ${fixed}px in a row that does not wrap; allow wrap or set stackBelow`)
      }
      if (node.type === 'grid' && isResponsiveMap(node.columns) && Number(node.columns.base ?? 1) > 2) {
        warn(p, 'responsive', `${node.columns.base} columns on a phone; start from base 1 or 2`)
      }

      const kids = Array.isArray(node.children) ? node.children : null
      if (kids && !(CONTAINER_TYPES as readonly string[]).includes(node.type)) {
        if (!kids.length && !OPTIONAL_CHILDREN.includes(node.type) && node.type !== 'list') warn(p, 'empty-panel', `the ${node.type} has no content`)
        visit(kids, `${p}.children`, narrow || node.type === 'row' || node.type === 'sidebar', sectionDepth)
      }

      if ((CONTAINER_TYPES as readonly string[]).includes(node.type)) {
        const children = Array.isArray(node.children) ? node.children : []
        if (!children.length) {
          warn(p, 'empty-panel', `the ${node.type} has no content`)
        }
        let childNarrow = narrow
        if (node.type === 'split') childNarrow = true
        if (node.type === 'grid') {
          const columns = typeof node.columns === 'number' ? node.columns : isResponsiveMap(node.columns) ? Math.max(...Object.values(node.columns).map(Number).filter(Number.isFinite), 0) : 0
          if (columns >= 2 || node.columns === 'auto') childNarrow = true
          if (children.length > GRID_MAX_CHILDREN) {
            warn(p, 'density', `${children.length} items in one grid; split them into sections so the eye can group them`)
          }
          const heavy = children.some((c: Node) => c && (c.type === 'chart' || c.type === 'table'))
          if (heavy && columns > GRID_MAX_HEAVY_COLUMNS) {
            warn(p, 'density', `${columns} columns of charts or tables leave each one too narrow to read; use ${GRID_MAX_HEAVY_COLUMNS} or fewer`)
          }
        }
        if (node.type === 'stack' && node.direction === 'horizontal') childNarrow = true
        const depth = node.type === 'section' ? sectionDepth + 1 : sectionDepth
        if (node.type === 'section' && depth > MAX_SECTION_DEPTH) {
          warn(p, 'density', `sections nested ${depth} deep; flatten the structure`)
        }
        visit(children, `${p}.children`, childNarrow, depth)
      }
    })
  }

  visit(body, 'body', false, 0)
  if (charts > MAX_CHARTS) {
    warn('body', 'density', `${charts} charts in one document; lead with the few that matter and move the rest to a separate report`)
  }
  return warnings
}

export function lintDocumentDesign (doc: unknown): DesignWarning[] {
  if (!doc || typeof doc !== 'object') return []
  const d = doc as { body?: unknown, preset?: string }
  return lintDesign(d.body, { preset: d.preset })
}
